import {
  Card,
  FlatActionList,
  Page,
  Stack,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { ProductActionsRootParamList } from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import { useProductFormBarcodeLookup } from "../../utils/barcode/hooks";
import {
  productEntityToFormData,
  useExportProductInboundRetailer,
  useRemoveAllVariantDataFromProduct,
  useRemoveProductFromParent,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import { compact } from "lodash";
import {
  Product,
  RetailerInboundResourceType,
} from "@towersystems/roam-common/lib/generated-types";
import { useFormContext } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { Linking } from "react-native";
import Constants from "expo-constants";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { useTranslation } from "../../../../utilities/i18n";
import React from "react";
import { useDownloadJson } from "../../../../utilities/download-json";
import { useGetFormattedInboundRetailerDataForResource } from "../../../../utilities/retailer-inbound/hooks";

export type ProductActionsScreenProps = StackScreenProps<
  ProductActionsRootParamList,
  "ProductActionsScreen"
>;

export const ProductActionsScreen = ({
  navigation,
}: ProductActionsScreenProps) => {
  const { t } = useTranslation("product");
  const { reset, getValues } = useFormContext();
  const { product } = useSelectedProductEntityContextProvider();
  const { loading, handleOnBarcodeLookup } = useProductFormBarcodeLookup();
  const { loading: exporting, sendProductInboundRetailer } =
    useExportProductInboundRetailer();
  const { download } = useDownloadJson();

  const { loading: loadingResourceData, getFormattedResourceData } =
    useGetFormattedInboundRetailerDataForResource();

  const { loading: removingProduct, handleRemoveProductFromParent } =
    useRemoveProductFromParent();

  const {
    handleRemoveAllVariantDataFromProduct,
    loading: removingVariantData,
  } = useRemoveAllVariantDataFromProduct();

  const { colors } = useThemeProvider();

  const authentication = useAuthenticationProvider();

  const handleGetFormattedResourceData = React.useCallback(
    async (productId: string) => {
      return getFormattedResourceData(
        RetailerInboundResourceType.PRODUCT,
        productId
      )
        .then((result) => {
          return download({
            data: result as any,
            fileName: `product_${productId}_${new Date().toISOString()}.json`,
          });
        })
        .catch((e) => {});
    },
    [loading]
  );

  function openImageManager() {
    Linking.openURL(
      `${Constants.manifest?.extra?.utilitiesUrl}/products/${product.id}?auth=${authentication.token}`
    );
  }

  let actions = compact([
    !product.variantParentId && {
      key: "choose_template",
      title: t("action.select_template.label"),
      disabled: loading,
      loading: loading,
      subTitle: t("action.select_template.description"),
      onPress: () =>
        navigation.navigate("ProductActionsSelectVariantTemplateRootScreen", {
          screen: "ProductActionsSelectVariantTemplateScreen",
        }),
    },
    product.variantParentId == product.id && {
      key: "create_child_variant",
      title: t("action.child_variant.label"),
      disabled: loading,
      loading: loading,
      subTitle: t("action.child_variant.description"),
      onPress: () =>
        navigation.navigate("ProductActionsCreateChildVariantScreen"),
    },

    product.variantParentId
      ? {
          key: "choose_parent",
          isdestructive: true,
          title: t("action.remove_from_parent.label"),
          disabled: removingProduct,
          loading: removingProduct,
          subTitle: t("action.remove_from_parent.description"),
          onPress: async () =>
            await handleRemoveProductFromParent(product.id, {
              successCallback: async (product: Product) => {
                const locationId = getValues("locationId");
                reset(productEntityToFormData(product, locationId), {
                  keepDirtyValues: true,
                });
                // will break animations if we don't wait
                await timeout(100); //for 1 sec delay
                navigation.pop();
                return;
              },
            }),
        }
      : {
          key: "choose_parent",
          title: t("action.choose_parent.label"),
          disabled: loading,
          loading: loading,
          subTitle: t("action.choose_parent.description"),
          onPress: () =>
            navigation.navigate("ProductActionsSelectParentProductScreen"),
        },

    {
      key: "1",
      title: t("action.lookup_barcode.label"),
      disabled: loading,
      loading: loading,
      subTitle: t("action.lookup_barcode.description"),
      onPress: () =>
        handleOnBarcodeLookup({
          onSuccessCallback: () => navigation.goBack(),
        }),
    },

    {
      key: "4",
      title: t("action.duplicate_product.label"),
      subTitle: t("action.duplicate_product.description"),
      onPress: () => navigation.navigate("ProductActionsCopyProductScreen"),
    },
    product.variantParentId == product.id && {
      key: "clear_variant_data",
      title: t("action.clear_variant_data.label"),
      subTitle: t("action.clear_variant_data.description"),
      destructive: true,
      disabled: loading || removingVariantData,
      onPress: async () => {
        await handleRemoveAllVariantDataFromProduct(product.id, {
          successCallback: async (product: Product) => {
            const locationId = getValues("locationId");
            reset(productEntityToFormData(product, locationId), {
              keepDirtyValues: true,
            });
            // will break animations if we don't wait
            await timeout(100); //for 1 sec delay
            navigation.pop();
            return;
          },
        });
      },
    },
    {
      key: "5",
      title: t("action.delete_product.label"),
      description: t("action.delete_product.description"),
      onPress: () => {},
      destructive: true,
      disabled: true,
    },
  ]);

  return (
    <Page title={t("more_actions_title")}>
      <Stack vertical>
        <Card title={t("card.general_actions.title")}>
          <FlatActionList items={actions} />
        </Card>
        <Card title={t("card.product_data.title")}>
          <FlatActionList
            items={[
              {
                key: "export_product",
                title: t("action.export_product.label"),
                subTitle: t("action.export_product.description"),
                onPress: () => sendProductInboundRetailer(product.id),
                disabled: exporting,
                loading: exporting,
              },
              {
                key: "download_json",
                title: t("action.download_json.label"),
                subTitle: t("action.download_json.description"),
                onPress: () => handleGetFormattedResourceData(product.id),
                disabled: loadingResourceData,
                loading: loadingResourceData,
              },
            ]}
          />
        </Card>
        <Card title={t("card.product_links.title")}>
          <FlatActionList
            items={[
              {
                key: "manage_images",
                title: t("action.manage_images.label"),
                subTitle: t("action.manage_images.label"),
                onPress: openImageManager,
                componentRight: () => (
                  <Feather
                    name="external-link"
                    size={24}
                    color={colors.primary}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
function timeout(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}
